import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';

const RetouchSelection = ({ model, counters, onRetouchClick, onRetouchQuantitySubmit, onRetouchDecrease }) => {
  const [retouches, setRetouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRetouch, setEditingRetouch] = useState(null);
  const [quantity, setQuantity] = useState('');

  const fetchRetouches = useCallback(async () => {
    if (!model || !model.id) return;
    try {
      const response = await axios.get(`${API_URL}/api/retouch-types/?model=${model.id}`);
      const activeRetouches = (response.data.results || response.data).filter(r => r.is_active);
      setRetouches(activeRetouches);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  }, [model]);

  useEffect(() => {
    if (model && model.id) {
      fetchRetouches();
    }
  }, [model, fetchRetouches]);

  const handleQuantitySubmit = (e, retouch) => {
    e.stopPropagation();
    const num = parseInt(quantity);
    if (num > 0 && num <= 999) {
      onRetouchQuantitySubmit(retouch, num);
      setQuantity('');
      setEditingRetouch(null);
    }
  };

  const handleQuantityClick = (e, retouch) => {
    e.stopPropagation();
    setEditingRetouch(retouch.id);
    setQuantity('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="loading-spinner h-12 w-12 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Chargement des retouches...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      <div className="text-center mb-8 sm:mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-blue-100 shadow-lg">
        <h2 className="text-3xl sm:text-4xl font-bold text-corporate-900 mb-2 sm:mb-3">
          Modèle: {model.name}
        </h2>
        <h3 className="text-lg sm:text-xl text-gray-600 font-medium">
          Cliquez sur le défaut ou saisissez le nombre
        </h3>
      </div>

      {retouches.length === 0 ? (
        <div className="text-center py-16 sm:py-20 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">🔧</div>
          <p className="text-gray-600 text-lg sm:text-2xl font-semibold">Aucune retouche disponible pour ce modèle</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
          {retouches.map((retouch, index) => {
            const count = counters[retouch.id] || 0;
            const isEditing = editingRetouch === retouch.id;
            
            return (
              <div key={retouch.id} className="relative animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <button
                  data-retouch-id={retouch.id}
                  onClick={() => onRetouchClick(retouch)}
                  className="card-hover p-4 sm:p-6 min-h-[240px] sm:min-h-[280px] w-full flex flex-col items-center
                           relative bg-white hover:bg-gray-50 transition-all duration-200 rounded-xl border-2 border-gray-200 hover:border-primary-400 shadow-md hover:shadow-xl"
                >
                  {/* Compteur amélioré avec boutons +/- - Toujours visible */}
                  <div className={`absolute top-2 right-2 px-3 py-1.5 rounded-full font-bold text-base shadow-lg z-10 flex items-center space-x-2 border-2 border-white transition-all duration-200 ${
                    count > 0 
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' 
                      : 'bg-gradient-to-br from-gray-400 to-gray-500 text-white'
                  }`}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (count > 0 && onRetouchDecrease) {
                          onRetouchDecrease(retouch);
                        }
                      }}
                      disabled={count === 0}
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                        count > 0 
                          ? 'bg-white/20 hover:bg-white/30 cursor-pointer' 
                          : 'bg-white/10 cursor-not-allowed opacity-50'
                      }`}
                      title="Diminuer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                      </svg>
                    </button>
                    <span className="text-lg min-w-[24px] text-center">{count}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetouchClick(retouch);
                      }}
                      className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      title="Augmenter"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>

                  {/* Image ou placeholder */}
                  {retouch.image_url ? (
                    <div className="relative w-full h-40 sm:h-48 mb-3 sm:mb-4 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={retouch.image_url}
                        alt={retouch.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 sm:h-48 mb-3 sm:mb-4 rounded-lg bg-gray-100 flex items-center justify-center text-5xl sm:text-6xl">
                      🔧
                    </div>
                  )}
                  
                  {/* Nom */}
                  <div className="text-sm sm:text-base font-bold text-corporate-900 text-center">
                    {retouch.name}
                  </div>
                </button>

                {/* Bouton quantité */}
                {isEditing ? (
                  <div className="mt-3 card p-3 flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleQuantitySubmit(e, retouch);
                        }
                      }}
                      autoFocus
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0"
                    />
                    <button
                      onClick={(e) => handleQuantitySubmit(e, retouch)}
                      className="w-12 h-12 bg-accent-600 text-white rounded-lg font-bold hover:bg-accent-700 transition-colors flex items-center justify-center shadow-corporate"
                      aria-label="Valider"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingRetouch(null);
                        setQuantity('');
                      }}
                      className="w-12 h-12 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors flex items-center justify-center shadow-corporate"
                      aria-label="Annuler"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => handleQuantityClick(e, retouch)}
                    className="mt-3 w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-lg font-bold text-base
                             hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105
                             flex items-center justify-center space-x-2"
                    title="Saisir un nombre"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Saisir quantité</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RetouchSelection;
